interface RgbColor {
  r: number;
  g: number;
  b: number;
}

const LIGHT_TEXT = '#FFFFFF';
const DARK_TEXT = '#111827';
const WHITE_RGB: RgbColor = { r: 255, g: 255, b: 255 };
const DARK_RGB: RgbColor = { r: 17, g: 24, b: 39 };

function parseHexColor(hex: string): RgbColor | null {
  const normalized = hex.trim().replace('#', '');
  const fullHex =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;

  if (!/^[0-9a-f]{6}$/i.test(fullHex)) {
    return null;
  }

  return {
    r: parseInt(fullHex.slice(0, 2), 16),
    g: parseInt(fullHex.slice(2, 4), 16),
    b: parseInt(fullHex.slice(4, 6), 16),
  };
}

function getRelativeLuminance({ r, g, b }: RgbColor): number {
  const toLinear = (channel: number) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function getContrastRatio(background: RgbColor, foreground: RgbColor): number {
  const backgroundLuminance = getRelativeLuminance(background);
  const foregroundLuminance = getRelativeLuminance(foreground);
  const lighter = Math.max(backgroundLuminance, foregroundLuminance);
  const darker = Math.min(backgroundLuminance, foregroundLuminance);

  return (lighter + 0.05) / (darker + 0.05);
}

function normalizeHex(hex: string): string {
  const trimmed = hex.trim();
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function rgbToHex({ r, g, b }: RgbColor): string {
  const channel = (value: number) =>
    Math.min(255, Math.max(0, value)).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function rgbToHsl({ r, g, b }: RgbColor): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
        break;
      case gn:
        h = ((bn - rn) / d + 2) / 6;
        break;
      default:
        h = ((rn - gn) / d + 4) / 6;
        break;
    }
  }

  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): RgbColor {
  if (s === 0) {
    const value = Math.round(l * 255);
    return { r: value, g: value, b: value };
  }

  const hue2rgb = (p: number, q: number, t: number) => {
    let channel = t;
    if (channel < 0) channel += 1;
    if (channel > 1) channel -= 1;
    if (channel < 1 / 6) return p + (q - p) * 6 * channel;
    if (channel < 1 / 2) return q;
    if (channel < 2 / 3) return p + (q - p) * (2 / 3 - channel) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  };
}

const NAV_SURFACE_LIGHT = '#FFFFFF';
const NAV_SURFACE_DARK = '#1F2937';

/** Contraste mínimo ícone/texto de navegação sobre fundo do sidebar (WCAG large ~3:1). */
const NAV_ACCENT_MIN_CONTRAST = 3;

const BRAND_LIGHTNESS_MIN = 0.08;
const BRAND_LIGHTNESS_MAX = 0.92;

/**
 * Ajusta uma cor da marca (clareia ou escurece mantendo matiz) até atingir contraste legível
 * sobre o fundo informado. Se já houver contraste suficiente, devolve a cor original.
 */
export function adjustBrandHexForSurfaceContrast(
  brandHex: string,
  surfaceHex: string,
  minRatio = NAV_ACCENT_MIN_CONTRAST
): string {
  const normalized = normalizeHex(brandHex);
  const bg = parseHexColor(surfaceHex);
  const fg = parseHexColor(normalized);
  if (!bg || !fg) {
    return normalized;
  }

  if (getContrastRatio(bg, fg) >= minRatio) {
    return normalized;
  }

  const hsl = rgbToHsl(fg);
  const shouldLighten = getRelativeLuminance(fg) <= getRelativeLuminance(bg);
  const step = 0.01;

  let bestHex = normalized;
  let bestContrast = getContrastRatio(bg, fg);

  if (shouldLighten) {
    for (let lightness = hsl.l + step; lightness <= BRAND_LIGHTNESS_MAX; lightness += step) {
      const candidate = hslToRgb(hsl.h, hsl.s, lightness);
      const ratio = getContrastRatio(bg, candidate);
      if (ratio >= minRatio) {
        return rgbToHex(candidate);
      }
      if (ratio > bestContrast) {
        bestContrast = ratio;
        bestHex = rgbToHex(candidate);
      }
    }
  } else {
    for (let lightness = hsl.l - step; lightness >= BRAND_LIGHTNESS_MIN; lightness -= step) {
      const candidate = hslToRgb(hsl.h, hsl.s, lightness);
      const ratio = getContrastRatio(bg, candidate);
      if (ratio >= minRatio) {
        return rgbToHex(candidate);
      }
      if (ratio > bestContrast) {
        bestContrast = ratio;
        bestHex = rgbToHex(candidate);
      }
    }
  }

  if (bestContrast < minRatio && hsl.s < 1) {
    const targetLightness = shouldLighten ? BRAND_LIGHTNESS_MAX : BRAND_LIGHTNESS_MIN;
    for (let saturation = hsl.s + 0.05; saturation <= 1; saturation += 0.05) {
      const candidate = hslToRgb(hsl.h, saturation, targetLightness);
      const ratio = getContrastRatio(bg, candidate);
      if (ratio >= minRatio) {
        return rgbToHex(candidate);
      }
      if (ratio > bestContrast) {
        bestContrast = ratio;
        bestHex = rgbToHex(candidate);
      }
    }
  }

  return bestHex;
}

/**
 * Cor de destaque (ícones/links inativos) legível sobre o sidebar.
 * Prioriza a primária do estabelecimento; se não atingir contraste mesmo ajustada, usa a secundária.
 * Sempre deriva das cores do tenant — nunca cinza fixo.
 */
export function pickNavBrandAccentHex(
  primaryHex: string,
  secondaryHex: string,
  isDark: boolean
): string {
  const surface = isDark ? NAV_SURFACE_DARK : NAV_SURFACE_LIGHT;

  const primaryAccent = adjustBrandHexForSurfaceContrast(primaryHex, surface);
  if (hexHasContrastOnSurface(primaryAccent, surface)) {
    return primaryAccent;
  }

  const secondaryAccent = adjustBrandHexForSurfaceContrast(secondaryHex, surface);
  if (hexHasContrastOnSurface(secondaryAccent, surface)) {
    return secondaryAccent;
  }

  const bg = parseHexColor(surface);
  const primaryRgb = parseHexColor(primaryAccent);
  const secondaryRgb = parseHexColor(secondaryAccent);
  if (bg && primaryRgb && secondaryRgb) {
    const primaryContrast = getContrastRatio(bg, primaryRgb);
    const secondaryContrast = getContrastRatio(bg, secondaryRgb);
    return secondaryContrast > primaryContrast ? secondaryAccent : primaryAccent;
  }

  return primaryAccent;
}

export function hexHasContrastOnSurface(
  fgHex: string,
  surfaceHex: string,
  minRatio = NAV_ACCENT_MIN_CONTRAST
): boolean {
  const fg = parseHexColor(fgHex);
  const bg = parseHexColor(surfaceHex);
  if (!fg || !bg) return false;
  return getContrastRatio(bg, fg) >= minRatio;
}

/**
 * Branco ou cinza muito escuro — o que tiver melhor contraste sobre o fundo (hex).
 * Para texto e rótulos em botões sobre `backgroundColor` da mesma cor.
 */
export function getReadableTextOnBackgroundHex(hex: string): string {
  const bg = parseHexColor(hex);
  if (!bg) {
    return LIGHT_TEXT;
  }
  const lightContrast = getContrastRatio(bg, WHITE_RGB);
  const darkContrast = getContrastRatio(bg, DARK_RGB);
  return lightContrast >= darkContrast ? LIGHT_TEXT : DARK_TEXT;
}

function blendRgb(a: RgbColor, b: RgbColor): RgbColor {
  return {
    r: Math.round((a.r + b.r) / 2),
    g: Math.round((a.g + b.g) / 2),
    b: Math.round((a.b + b.b) / 2),
  };
}

/**
 * Branco ou cinza escuro — o que maximizar o contraste mínimo no gradiente diagonal
 * (primária, secundária e ponto médio), para texto sobre `linear-gradient(135deg, …)`.
 */
export function getReadableTextOnBrandGradientHex(
  primaryHex: string,
  secondaryHex: string
): string {
  const p = parseHexColor(primaryHex);
  const s = parseHexColor(secondaryHex);
  if (!p || !s) {
    return LIGHT_TEXT;
  }
  const mid = blendRgb(p, s);
  const surfaces = [p, s, mid];
  let bestText = LIGHT_TEXT;
  let bestMinContrast = -1;
  for (const candidateHex of [LIGHT_TEXT, DARK_TEXT]) {
    const fg = candidateHex === LIGHT_TEXT ? WHITE_RGB : DARK_RGB;
    const minContrast = Math.min(...surfaces.map((bg) => getContrastRatio(bg, fg)));
    if (minContrast > bestMinContrast) {
      bestMinContrast = minContrast;
      bestText = candidateHex;
    }
  }
  return bestText;
}

/** RGBA usando `--establishment-gradient-glass-rgb` (vidro sobre gradiente tenant). */
export function establishmentGradientGlass(alpha: number): string {
  return `rgba(var(--establishment-gradient-glass-rgb), ${alpha})`;
}

/** RGB para `rgba(var(--establishment-gradient-glass-rgb), α)` em vidros sobre o gradiente. */
export function gradientGlassRgbForForeground(foregroundHex: string): string {
  const n = foregroundHex.trim().toLowerCase();
  return n === '#111827' || n === 'rgb(17, 24, 39)' ? '17, 24, 39' : '255, 255, 255';
}
